import { Swiper, SwiperSlide } from "swiper/react";
import { Pagination } from "swiper/modules";
import React, { useEffect, useRef, useState } from "react";
import "swiper/css";
import "swiper/css/pagination";
import { LifestyleWinner } from "../types";
import { getLifestyleWinners } from "../car-service";
import DOMPurify from "dompurify";
const resolveWinnerPhoto = (winner: LifestyleWinner) => {
  const url = winner.CloseUpPhoto || winner.Photo;
  if (!url) return "";
  if (url.startsWith("http")) return url;
  return `${process.env.NEXT_PUBLIC_HOST}${url}`;
};

const WinnersSlider = () => {
  const swiperRef = useRef(null);
  const [winners, setWinners] = useState<LifestyleWinner[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;

    const getWinnersData = async () => {
      try {
        const data = await getLifestyleWinners("XC");
        if (cancelled) return;
        setWinners((data?.winnerList ?? []).slice(0, 5));
      } catch (error) {
        if (cancelled) return;
        console.error("Error fetching XC winners data:", error);
      } finally {
        if (!cancelled) {
          setIsLoading(false);
        }
      }
    };

    getWinnersData();

    return () => {
      cancelled = true;
    };
  }, []);

  if (isLoading || winners.length === 0) {
    return null;
  }

  return (
    <div className="flex flex-col items-center gap-3 lg:gap-5 w-full font-jost">
      <div className="w-full text-center text-base lg:text-2xl font-semibold leading-5.5 lg:leading-[30px] lg:[text-shadow:0_2px_4px_rgba(0,0,0,0.70)]">
        <span className="text-dark-1 lg:text-white">🏆 </span>
        <span className="text-primary">18 luxury cars</span>
        <span className="text-dark-1 lg:text-white"> won in the last 90 days</span>
      </div>

      <div className="flex flex-col items-center gap-3 lg:gap-5 w-full bg-gradient-to-b from-[rgba(10,14,24,0.97)] to-[rgba(22,34,56,0.97)] border-0 lg:border lg:border-solid lg:border-white/8 px-3 lg:px-6 pt-5 lg:pt-4 pb-6 lg:pb-4 rounded-[10px] lg:rounded-2xl overflow-hidden">
        <div className="w-full text-center text-[11px] lg:text-[13px] font-medium leading-3.5 lg:leading-6 tracking-wide lg:tracking-[0.78px] uppercase">
          <span className="text-white">What our </span>
          <span className="text-primary">luxury car winners</span>
          <span className="text-white"> say</span>
        </div>

        <div className="relative w-full pb-6">
          <Swiper
            modules={[Pagination]}
            slidesPerView={1}
            loop={false}
            spaceBetween={24}
            pagination={{
              el: "#xc-winners-pagination",
              clickable: true,
            }}
            onSwiper={swiper => {
              swiperRef.current = swiper;
            }}
            className="min-w-0">
            {winners.map((winner, index) => (
              <SwiperSlide key={`xc-winner-${index}`}>
                <div className="flex items-start gap-3">
                  <img
                    src={resolveWinnerPhoto(winner)}
                    alt=""
                    width={64}
                    height={64}
                    className="size-14 lg:size-16 shrink-0 aspect-[1/1] rounded-full border-[1.877px] lg:border-2 border-solid border-primary object-cover"
                  />
                  <div className="flex flex-col items-start gap-4 min-w-0 w-full">
                    <div
                      dangerouslySetInnerHTML={{
                        __html: DOMPurify.sanitize(winner.Testimonial || winner.StoryPreview || ""),
                      }}
                      className="self-stretch text-[#BCBCC0] text-[13px] lg:text-base italic font-medium leading-[18px] lg:leading-5.5 line-clamp-3 [&_p]:m-0"
                    />
                    <div className="flex flex-col items-start self-stretch">
                      <div className="self-stretch text-white text-[13px] lg:text-lg font-bold leading-[18px] lg:leading-6">
                        {winner.Name}
                      </div>
                      <div className="self-stretch text-[#BCBCC0] text-[13px] lg:text-base font-medium leading-[18px] lg:leading-5.5">
                        {winner.Prize}
                      </div>
                    </div>
                  </div>
                </div>
              </SwiperSlide>
            ))}
          </Swiper>
          <div
            id="xc-winners-pagination"
            className="absolute !bottom-0 z-[2] !left-1/2 -translate-x-1/2 flex items-center justify-center gap-2"
          />
        </div>
      </div>
    </div>
  );
};

export default WinnersSlider;
